/** Reading and checking `hotpot-cms.yaml`. Errors are plain sentences for the studio developer. */
import type { Collection, Field, FieldType, SiteConfig } from './config.js';
import { resolveItem } from './config.js';
import { parseYaml } from './yaml-edit.js';

const FIELD_TYPES: FieldType[] = [
  'string', 'text', 'markdown', 'image', 'gallery', 'date', 'datetime', 'number', 'boolean', 'select',
  'relation', 'list', 'object', 'url', 'youtube', 'color', 'file', 'hidden',
];

export class ConfigError extends Error {
  constructor(public errors: string[]) {
    super(`hotpot-cms.yaml has ${errors.length} problem(s):\n- ${errors.join('\n- ')}`);
    this.name = 'ConfigError';
  }
}

export function loadConfig(text: string): SiteConfig {
  const data = parseYaml(text) as unknown as SiteConfig;
  const errors = checkConfig(data);
  if (errors.length) throw new ConfigError(errors);
  return data;
}

export function checkConfig(config: SiteConfig): string[] {
  const errors: string[] = [];
  if (!config || typeof config !== 'object') return ['The file is empty or not a mapping.'];
  if (config.version !== 1) errors.push('`version: 1` is missing.');
  if (!config.site?.name) errors.push('`site.name` is missing.');
  if (!config.media?.folder || !config.media?.public) errors.push('`media.folder` and `media.public` are required.');
  if (!Array.isArray(config.collections) || !config.collections.length) {
    errors.push('`collections` must list at least one collection.');
    return errors;
  }
  const names = new Set<string>();
  for (const c of config.collections) {
    if (!c?.name) {
      errors.push('A collection has no `name`.');
      continue;
    }
    if (names.has(c.name)) errors.push(`Collection "${c.name}" is defined twice.`);
    names.add(c.name);
    checkCollection(c, errors);
  }
  if (config.i18n) {
    const { locales, structure } = config.i18n;
    if (!Array.isArray(locales) || !locales.length) errors.push('`i18n.locales` must list the languages.');
    else if (!locales.includes(config.i18n.default)) errors.push('`i18n.default` must be one of `i18n.locales`.');
    if (!['field_suffix', 'multiple_files'].includes(structure)) errors.push(`Unknown i18n.structure "${structure}".`);
  }
  for (const item of [...(config.navigation ?? []).flatMap((g) => g.items ?? []), ...(config.settings ?? [])]) {
    if (!resolveItem(config, item)) errors.push(`Navigation item "${item}" doesn't match a collection or file.`);
  }
  for (const s of config.start?.shortcuts ?? []) {
    if (s.target && !resolveItem(config, s.target)) errors.push(`Start shortcut "${s.label}" points to unknown "${s.target}".`);
  }
  // Relations must point somewhere real.
  const walk = (fields: Field[], where: string) => {
    for (const f of fields ?? []) {
      if (f.type === 'relation' && !resolveItem(config, f.collection)) {
        errors.push(`${where}.${f.name}: relation to unknown "${f.collection}".`);
      }
      if (f.type === 'object' || f.type === 'list') walk((f as any).fields ?? [], `${where}.${f.name}`);
      if (f.type === 'list') for (const t of f.types ?? []) walk(t.fields, `${where}.${f.name}.${t.name}`);
    }
  };
  for (const c of config.collections) {
    if (c.type === 'folder') walk(c.fields, c.name);
    else for (const f of c.files ?? []) walk(f.fields, `${c.name}/${f.name}`);
  }
  return errors;
}

function checkCollection(c: Collection, errors: string[]) {
  if (c.type === 'folder') {
    if (!c.folder) errors.push(`${c.name}: \`folder\` is missing.`);
    if (!c.slug) errors.push(`${c.name}: \`slug\` is missing.`);
    if (!c.title_field) errors.push(`${c.name}: \`title_field\` is missing.`);
    if (/^\/|\.\./.test(c.folder ?? '')) errors.push(`${c.name}: folder must be a repo path without "/" at the start or "..".`);
    if (c.path !== undefined && (!c.path.includes('{{slug}}') || /^\/|\.\./.test(c.path))) {
      errors.push(`${c.name}: path must contain {{slug}} and stay inside the folder (e.g. "{{slug}}/index").`);
    }
    checkFields(c.fields, c.name, errors);
    const names = new Set((c.fields ?? []).map((f) => f.name));
    if (c.title_field && !names.has(c.title_field)) errors.push(`${c.name}: title_field "${c.title_field}" is not a field.`);
    if (c.draft_field && !names.has(c.draft_field)) errors.push(`${c.name}: draft_field "${c.draft_field}" is not a field.`);
    if (c.order_field && !names.has(c.order_field.field)) errors.push(`${c.name}: order_field "${c.order_field.field}" is not a field.`);
  } else if (c.type === 'files') {
    if (!Array.isArray(c.files) || !c.files.length) errors.push(`${c.name}: \`files\` must list at least one file.`);
    for (const f of c.files ?? []) {
      if (!f.name || !f.file) errors.push(`${c.name}: every file needs \`name\` and \`file\`.`);
      if (/^\/|\.\./.test(f.file ?? '')) errors.push(`${c.name}/${f.name}: file must be a repo path without "/" at the start or "..".`);
      checkFields(f.fields, `${c.name}/${f.name}`, errors);
    }
  } else {
    errors.push(`${(c as Collection).name}: \`type\` must be "folder" or "files".`);
  }
}

function checkFields(fields: Field[], where: string, errors: string[]) {
  if (!Array.isArray(fields)) {
    errors.push(`${where}: \`fields\` is missing.`);
    return;
  }
  const seen = new Set<string>();
  for (const f of fields) {
    if (!f?.name) {
      errors.push(`${where}: a field has no name.`);
      continue;
    }
    if (seen.has(f.name)) errors.push(`${where}.${f.name}: defined twice.`);
    seen.add(f.name);
    if (!FIELD_TYPES.includes(f.type)) errors.push(`${where}.${f.name}: unknown type "${f.type}".`);
    if (f.type === 'select' && !Array.isArray(f.options)) errors.push(`${where}.${f.name}: select needs \`options\`.`);
    if (f.type === 'object') checkFields(f.fields, `${where}.${f.name}`, errors);
    if ((f.type === 'object' || f.type === 'list') && f.switch) {
      const names = new Set((f.fields ?? []).map((x) => x.name));
      if (!Array.isArray(f.switch) || f.switch.length < 2) errors.push(`${where}.${f.name}: \`switch\` needs at least two options.`);
      else for (const o of f.switch) {
        if (!o?.label || !Array.isArray(o.fields) || !o.fields.length) errors.push(`${where}.${f.name}: every switch option needs a \`label\` and \`fields\`.`);
        else for (const n of o.fields) if (!names.has(n)) errors.push(`${where}.${f.name}: switch option "${o.label}" uses unknown field "${n}".`);
      }
    }
    if (f.type === 'list' && f.fields) checkFields(f.fields, `${where}.${f.name}`, errors);
    if (f.type === 'list' && f.types) {
      if (!Array.isArray(f.types) || !f.types.length) errors.push(`${where}.${f.name}: \`types\` must list at least one type.`);
      else for (const t of f.types) {
        if (!t?.name) errors.push(`${where}.${f.name}: every type needs a \`name\`.`);
        else checkFields(t.fields, `${where}.${f.name}.${t.name}`, errors);
      }
    }
    if ((f.type === 'file' || f.type === 'gallery') && f.storage) {
      if (f.storage.type !== 'r2' || !f.storage.bucket) errors.push(`${where}.${f.name}: storage needs \`type: r2\` and a \`bucket\`.`);
      if (!/^https:\/\//.test(f.storage.public_url ?? '')) errors.push(`${where}.${f.name}: storage.public_url must be an https address.`);
    }
    if (f.pattern) {
      try {
        new RegExp(f.pattern[0], 'u');
      } catch {
        errors.push(`${where}.${f.name}: pattern is not a valid regular expression.`);
      }
    }
  }
}
